import { Marquee } from "../../art/Marquee.js";
import { ShaderSurface, surfaceNames } from "../../art/ShaderSurface.js";
import "../../art/surfaces.js";
import { ArtView, type ArtParam } from "../../art/ArtView.js";
import { compileDrawing, parseViewBox } from "../../art/markup/drawing.js";
import { ExpressionError } from "../../art/markup/expr.js";
import { MarkupError } from "../errors.js";
import { CONTROL_ATTRS } from "../schema.js";
import { addToControls, registerElement, type AttrTable, type Attrs } from "../registry.js";

/** What every piece of art takes. */
const ART: AttrTable = { speed: "number", seed: "number", animate: "boolean" };

const opts = <T>(attrs: Attrs): T => attrs as T;

const ART_VIEW: AttrTable = {
  ...ART,
  ref: "string",
  viewBox: "string",
  fit: ["contain", "cover", "stretch", "none"],
  overflow: "number",
  // Anything else is a parameter of the definition `ref` names.
  "*": "string",
};

const NOT_PARAMS = new Set([...Object.keys(CONTROL_ATTRS), ...Object.keys(ART_VIEW)]);

export function registerArtElements(): void {
  /**
   * `<Art>`: a drawing in art markup. Its children are the drawing - shapes, generators, `<Use>` -
   * or `ref` names a `<Define>` and every other attribute is one of its parameters.
   */
  registerElement<ArtView>({
    tag: "Art",
    attributes: ART_VIEW,
    create: (attrs, ctx, element) => {
      const params: Record<string, ArtParam> = {};
      for (const [k, v] of Object.entries(attrs)) if (!NOT_PARAMS.has(k)) params[k] = v as ArtParam;
      const viewBox = typeof attrs["viewBox"] === "string" ? parseViewBox(attrs["viewBox"], element) : null;
      try {
        return new ArtView({
          ...(attrs as object),
          viewBox,
          params,
          variables: ctx.variables,
        });
      } catch (e) {
        if (e instanceof ExpressionError) throw new MarkupError(`<Art> parameter: ${e.message}`, element);
        throw e;
      }
    },
    content: (art, children) => {
      if (children.length > 0) art.drawing = compileDrawing(children);
    },
  });

  registerElement<ShaderSurface>({
    tag: "ShaderSurface",
    attributes: {
      ...ART,
      preset: surfaceNames(false),
      colorA: "colorRef",
      colorB: "colorRef",
      colorC: "colorRef",
      intensity: "number",
      scale: "number",
      extra: "number",
    },
    create: (attrs) => new ShaderSurface(opts(attrs)),
  });

  registerElement<Marquee>({
    tag: "Marquee",
    base: "container",
    attributes: { speed: "number", gap: "number", pauseOnHover: "boolean" },
    create: (attrs) => new Marquee(opts(attrs)),
    addChild: (marquee, child) => addToControls(marquee, child),
  });
}
